import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { AppConfigService } from '@core/appconfig.service';
import { UserService } from '@core/user.service';
import { School } from '@models/school';
import { NgbModal, NgbModalRef, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { RoleType } from '@shared/enums/role-type';
import { FallbackDownloadComponent } from '@shared/fallback-download/fallback-download.component';
import { Helpers } from '@shared/helpers';
import {
  DeleteAllUsersModalOutput
} from '@shared/manage-users/branded-manage-users/delete-all-users-modal/delete-all-users-modal-types';
import {
  DeleteAllUsersModalComponent
} from '@shared/manage-users/branded-manage-users/delete-all-users-modal/delete-all-users-modal.component';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, combineLatest, from, map, of, skipWhile, Subscription } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';
import { SchoolsFacade } from '../../../schools/store/schools.facade';
import {
  CreateStudentPasswordsModalComponent
} from '../students/create-student-passwords-modal/create-student-passwords-modal.component';

@Component({
  selector: 'zbp-branded-manage-users',
  templateUrl: './branded-manage-users.component.html',
  styleUrls: ['./branded-manage-users.component.scss'],
  standalone: false
})
export class BrandedManageUsersComponent implements OnInit, OnDestroy {
  private defaultDeleteAllText = 'Delete users';
  private defaultChangeAllPasswordsText = 'Create new passwords';
  private hasSchoolIdParam = false;
  private _distSchool: School = <School>{
    educationalUnitId: '00000000-0000-0000-0000-000000000000',
    schoolId: null,
    name: '--District Wide--',
    abbreviatedSchoolId: 'distWide',
    hasAvailableLicenses: false,
    hasFutureActivation: false,
    hasSchoolUserRoles: false,
    isOneRoster: false
  };
  districtId: string;
  modalRef: NgbModalRef;
  schools: School[] = [];
  school: School = null;
  selectedSchool: BehaviorSubject<School> = new BehaviorSubject<School>(null);
  form: FormGroup;
  schoolIdToolTipMessage: string = Helpers.schoolIdToolTipMessage;
  hasSchoolIdSet: boolean = false;
  subscriptions: Subscription[] = [];
  iconNames = zbpIconNames;
  districtWideSelected: boolean = false;
  isOneRoster: boolean = false;
  addUsersTooltipText: string = '';
  changeAllStudentsPasswordText: string = this.defaultChangeAllPasswordsText;
  importUsersTooltipText: string = '';
  exportUsersTooltipText: string = '';
  deleteAllUsersTooltipText: string = this.defaultDeleteAllText;
  isManagingStudent: boolean = false;
  showAllColumns: boolean = false;
  isManagingTeachers: boolean = false;
  isManagingAdmins: boolean = false;
  isManagingStudents: boolean = false;
  isManagingParents: boolean = false;
  isExporting: boolean = false;
  showExportSpinner: boolean = false;
  pageTitle: string = 'User Management';

  protected readonly zbpIconNames = zbpIconNames;

  @ViewChild('exportDownload') exportDownload: FallbackDownloadComponent;

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private userService: UserService,
    private toastr: ToastrService,
    private schoolsFacade: SchoolsFacade,
    private formBuilder: FormBuilder,
    private appConfig: AppConfigService,
    private title: Title,
    private appConfigService: AppConfigService,
  ) { }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
  ngOnInit() {
    let queryParams: Params;
    if (!this.appConfig.isHighlightsPortalUrl) {
      this.showAllColumns = true;
    }
    this.title.setTitle(`${this.pageTitle} | ${this.appConfigService.getBrandTitle}`);

    this.form = this.formBuilder.group({
      userSearchCriteria: ['', []],
    });

    // When the selected school changes, reset the school
    this.subscriptions.push(
      this.selectedSchool
        .subscribe((school: School) => {
          this.school = school;
          if (this.school) {
            this.hasSchoolIdSet = !!this.school?.schoolId && !!this.school?.abbreviatedSchoolId;
            queryParams = {
              userSearchCriteria: '',
              schoolId: this.school.schoolId,
              districtId: this.school.districtId,
              educationalUnitId: this.school.educationalUnitId,
              page: 1,
            };

            this.setToolTipTextForSchoolIds();

            //send districtId in query param so API will search district wide when district-wide distSchool is selected
            if (this.school.educationalUnitId === this._distSchool.educationalUnitId) {
              queryParams['districtId'] = this.school.districtId;
              this.districtWideSelected = true;
            } else {
              this.districtWideSelected = false;
            }

            this.districtId = this.school.districtId;
            this.isOneRoster = this.school?.isOneRoster;
            this.router.navigate([], { relativeTo: this.route.parent, queryParams, queryParamsHandling: 'merge' });
          }
          this.form.get('userSearchCriteria').reset();
        }),
      this.schoolsFacade.schools$
        .pipe(
          take(1),
          mergeMap((schools: School[]) => {
            this.schools = schools.map(s => s);
            // Add distSchool if it is a districtAdmin login and
            // if it doesn't exist on schools list already for district wide search on DDL
            if (this.schools.length > 0
            && this.userService.user.isDistrictAdmin
            && this.schools.findIndex(s => s.educationalUnitId === this._distSchool.educationalUnitId) === -1) {
              this._distSchool.districtId = this.schools[0].districtId;
              this._distSchool.isOneRoster = this.schools[0].isOneRoster;
              this.schools.push(this._distSchool);
            }
            return this.route.parent.params;
          }),
          mergeMap((params: Params) => {
            if (params.schoolId) {
              this.hasSchoolIdParam = true;
              return this.schoolsFacade.get(params.schoolId);
            }
            return of(null);
          })
        )
        .subscribe((school) => {
          if (this.hasSchoolIdParam) {
            this.selectedSchool.next(school);
          } else if (this.schools?.length > 0) {
            this.ensureSchoolIdSelector();
            this.onSchoolSelected();
          }
        }),
      // Updates query params when navigating to a different table from the header navigation link
      // Query params will cause the new table to load its data from the API
      // The tables are child routes of the Manage Users page
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          queryParams = {
            userSearchCriteria: this.form.get('userSearchCriteria')?.value,
            schoolId: this.school.schoolId,
            districtId: this.school.districtId,
            educationalUnitId: this.school.educationalUnitId,
          };

          //send districtId in query param so that the API will search district wide when distSchool is selected
          if (this.school?.educationalUnitId === this._distSchool?.educationalUnitId) {
            queryParams['districtId'] = this.school.districtId;
            this.districtWideSelected = true;
            this.setToolTipTextForDistrictWide();
          } else {
            this.districtWideSelected = false;
          }
          this.router.navigate([], { relativeTo: this.route, queryParams, queryParamsHandling: 'merge' });

          this.isManagingStudent = this.isManaging(this.routeIdentifiers.students);
          this.updateTableNavigationIndicators();
        }),
    );
  }
  private routeIdentifiers = {
    schoolAdmins: 'admins',
    teachers: 'teachers',
    students: 'students',
    parents: 'parents',
  };

  private updateTableNavigationIndicators() {
    this.isManagingTeachers = this.isManaging('teachers');
    this.isManagingAdmins = this.isManaging('admins');
    this.isManagingStudents = this.isManaging('students');
    this.isManagingParents = this.isManaging('parents');
  }

  private setToolTipTextForSchoolIds() {
    this.addUsersTooltipText = !this.hasSchoolIdSet ? this.schoolIdToolTipMessage : '';
    this.importUsersTooltipText = !this.hasSchoolIdSet ? this.schoolIdToolTipMessage : null;
    this.exportUsersTooltipText = !this.hasSchoolIdSet ? this.schoolIdToolTipMessage : null;
    this.deleteAllUsersTooltipText = !this.hasSchoolIdSet ? this.schoolIdToolTipMessage : this.defaultDeleteAllText;
    this.changeAllStudentsPasswordText = !this.hasSchoolIdSet
      ? this.schoolIdToolTipMessage
      : this.defaultChangeAllPasswordsText;
  }

  private districtWideDisabledTooltips = {
    addUser: 'You must select a specific school in your district before adding users.',
    importUsers: 'You must select a specific school in your district before importing users.',
    exportUsers: 'You must select a specific school in your district before exporting users.',
    userSettings: 'You must select a specific school in your district before updating user settings.',
    deleteAll: 'You must select a specific school in your district before deleting all users.',
    changeAllStudentsPassword:
      'You must select a specific school in your district before creating new passwords.',
  };

  private setToolTipTextForDistrictWide() {
    this.addUsersTooltipText = this.districtWideDisabledTooltips.addUser;
    this.importUsersTooltipText = this.districtWideDisabledTooltips.importUsers;
    this.exportUsersTooltipText = '';
    this.deleteAllUsersTooltipText = this.districtWideDisabledTooltips.deleteAll;
    this.changeAllStudentsPasswordText = this.districtWideDisabledTooltips.changeAllStudentsPassword;
  }

  get hasSchoolSelector(): boolean {
    return !!this.form.get('schoolId');
  }

  get importQueryParams(): Params {
    return this.hasSchoolSelector ? { schoolId: this.school?.schoolId } : null;
  }

  get canManageParents(): boolean {
    return this.userService.user.profileDetail.canManageParent;
  }

  isManaging(path: string): boolean {
    return this.router.url.includes(`/users/${path}`);
  }

  onSchoolSelected(): void {
    const schoolId = this.form.get('schoolId').value;
    const school = this.schools.find(s => s.schoolId === schoolId);
    this.selectedSchool.next(school);
  }

  goBack() {
    this.router.navigate(['..', 'overview'], { relativeTo: this.route });
  }

  export(): void {
    this.showExportSpinner = true;
    this.isExporting = true;
    let fileName = '';
    let role: RoleType;
    // Figures out role from URL.
    const matches = this.router.url.match(/(teachers|admins|students|parents)/i);
    if (matches) {
      switch (matches[0]) {
      case this.routeIdentifiers.schoolAdmins:
        role = RoleType.SchoolAdministrator;
        fileName = 'import_admins';
        break;
      case this.routeIdentifiers.parents:
      case this.routeIdentifiers.students:
        role = RoleType.Student;
        fileName = 'import_students';
        break;
      case this.routeIdentifiers.teachers:
      default:
        role = RoleType.Teacher;
        fileName = 'import_teachers';
        if (this.appConfig.isHighlightsPortalUrl) {
          fileName += '_hightlights';
        }
        break;
      }
    }

    const { schoolId } = this.school;
    this.userService.export(schoolId ?? null, role, '',  '', this.districtId)
      .pipe(
        mergeMap((file?: Blob) => Helpers.downloadFile(this.exportDownload, fileName, file))
      )
      .subscribe({
        next: (success) => {
          if (!success) {
            this.toastr.error('An error occurred exporting this report.');
          } else {
            this.toastr.success('Exported successfully.');
          }
        },
        error: () => this.toastr.error('An error occurred exporting this report.'),
        complete: () => {
          this.isExporting = false;
          this.showExportSpinner = false;
        }
      });
  }

  search(): void {
    const queryParams: Params = {
      userSearchCriteria: this.form.get('userSearchCriteria').value,
      schoolId: this.school.schoolId,
    };
    this.router.navigate([], { relativeTo: this.route, queryParams, queryParamsHandling: 'merge' });
  }

  // User types that can be added: School Admin, Teacher
  newUser(tooltip: NgbTooltip): void {
    if (this.hasSchoolIdSet) {
      let roleType: RoleType = RoleType.Teacher;
      // Figures out role from URL.
      const matches = this.router.url.match(/(teachers|admins)/i);
      if (matches) {
        switch (matches[0]) {
        case this.routeIdentifiers.schoolAdmins:
          roleType = RoleType.SchoolAdministrator;
          break;
        case this.routeIdentifiers.teachers:
        default:
          roleType = RoleType.Teacher;
          break;
        }
      }

      const queryParams = this.hasSchoolIdParam ? { roleType } : { roleType, schoolId: this.school.schoolId };
      this.router.navigate(['create-users'], { relativeTo: this.route.parent, queryParams });
    } else if (!tooltip.isOpen()) {
      tooltip.open();
    }
  }

  deleteAll(): void {
    if (this.hasSchoolIdSet) {
      this.modalRef = DeleteAllUsersModalComponent.open(this.modalService, {
        schoolName: this.school.name,
        options: { size: 'md' }
      });

      from(this.modalRef.result as Promise<DeleteAllUsersModalOutput>)
        .pipe(
          map(result => result.userRolesToDelete),
          skipWhile(userRolesToDelete => userRolesToDelete.length <= 0),
          mergeMap(userRolesToDelete => combineLatest([
            this.userService.deleteAllUsersFromRoles(this.school.schoolId, userRolesToDelete),
            of(userRolesToDelete),
          ])),
        )
        .subscribe(([res, userRolesDeleted]) => {
          if (res.success) {
            this.toastr.success(this.buildDeleteSuccessMessage(userRolesDeleted));
          } else {
            this.toastr.error(res.messages[0]);
          }
          // Reset the current users view.
          const queryParams: Params = { schoolId: this.school.schoolId };
          this.router.navigate([this.routeIdentifiers.teachers], { relativeTo: this.route, queryParams });
        });
    }
  }

  private buildDeleteSuccessMessage(userRolesDeleted: RoleType[]): string {
    /* Hardcoded to 'en' locale in order to meet requirements for generating a comma separated list,
    example: "A, B, and C". Ideally this would be based on the user's locale, but could have unintended
    consequences, since the entire site does not handle locales. */
    const listFormat = new Intl.ListFormat('en');
    const rolesDisplay = listFormat.format(userRolesDeleted.map(role => role.toLowerCase()));
    return `Successfully removed all ${rolesDisplay} users.`;
  }

  private ensureSchoolIdSelector(): void {
    if (!this.form.get('schoolId')) {
      this.form.addControl('schoolId', new FormControl(this.schools[0].schoolId, Validators.required));
      this.subscriptions.push(
        this.form.get('schoolId').valueChanges
          .subscribe(() => {
            this.onSchoolSelected();
          }),
      );
    }
  }

  importUsers(tooltip: NgbTooltip): void {
    if (this.hasSchoolIdSet) {
      this.router.navigate(['../import'], {
        relativeTo: this.route,
        queryParams: this.importQueryParams
      });
    } else {
      tooltip.open();
    }
  }

  openNewUser() {
    // Do not allow adding users if an account is oneRoster and user cannot manage parents
    if (this.hasSchoolIdSet && (!this.isOneRoster || this.canManageParents)) {
      let roleType: RoleType = RoleType.Teacher;
      // Figures out role from URL.
      const matches = this.router.url.match(/(teachers|students|admins|parents)/i);
      if (matches) {
        switch (matches[0]) {
        case this.routeIdentifiers.students:
          roleType = RoleType.Student;
          break;
        case this.routeIdentifiers.schoolAdmins:
          roleType = RoleType.SchoolAdministrator;
          break;
        case this.routeIdentifiers.parents:
          roleType = RoleType.Parent;
          break;
        case this.routeIdentifiers.teachers:
        default:
          roleType = RoleType.Teacher;
          break;
        }
      }
      const queryParams = this.hasSchoolIdParam ? { roleType } : { roleType, schoolId: this.school.schoolId };

      this.router.navigate(['create-users'], { relativeTo: this.route.parent, queryParams });
    }
  }

  openCreateAllStudentsPasswordModal() {
    this.modalRef = this.modalService.open(CreateStudentPasswordsModalComponent, { size: 'lg' });
    this.modalRef.componentInstance.school = this.school;

    from(this.modalRef.result)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }
}
